import React from 'react';
import { StyleSheet, View } from 'react-native';

import { useActionSheet } from '@components/ActionSheet';
import { UserAvatar } from '@components/Avatar';
import Divider from '@components/Divider';
import Explorer, { useExplorerMapDataRefreshKey } from '@components/Explorer';
import type { SearchTargets } from '@components/Explorer';
import JoinGroupButton from '@components/Group/JoinButton';
import Icon from '@components/Icon';
import IconButton from '@components/IconButton';
import Placeholder from '@components/Placeholders';
import { PressableOpacity } from '@components/Pressable';
import ScreenContainer from '@components/ScreenContainer';
import { Caption, Title } from '@components/Typography';
import { useCurrentUser } from '@context/UsersContext';
import { listenToGroup } from '@db/Groups';
import { useGroupMembersQuery } from '@db/Users/Queries';
import { logError } from '@helpers/Loggers';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { createAppNavigation } from '@navigation/AppNavigation';
import { ViewGroupScreenProps } from '@navigation/Types';
import { Group } from '@types';

const GROUP_HEADER_AVATAR_SIZE = 64;
const GROUP_HEADER_ICON_SIZE = 16;
const GROUP_SEARCH_TARGETS: SearchTargets = { users: true, groups: false, places: true };

export default function ViewGroupScreen({ route, navigation }: ViewGroupScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);
  const { userDoc } = useCurrentUser();
  const currentUserId = getCurrentUserId();

  const groupId = route.params?.groupId ?? '';
  const [loadingGroup, setLoadingGroup] = React.useState<boolean>(true);
  const [group, setGroup] = React.useState<Group | undefined>(undefined);
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const mapDataRefreshKey = useExplorerMapDataRefreshKey({ navigation, route });
  const isMember = Boolean(groupId && (userDoc?.groupIds || []).includes(groupId));
  const canViewGroupContent = isMember || isAdmin;
  const membersQuery = useGroupMembersQuery({
    enabled: canViewGroupContent,
    groupId,
  });
  const members = membersQuery.members;
  const actionSheetCallback = React.useCallback(
    (selectedIndex?: number) => {
      if (selectedIndex === 0) {
        appNavigation.openEditGroup(groupId);
      }
    },
    [appNavigation, groupId]
  );
  const { showActionSheet } = useActionSheet(
    {
      options: ['Edit Group', 'Cancel'],
      cancelButtonIndex: 1,
    },
    actionSheetCallback
  );
  const explorerUserIds = React.useMemo(() => members.map((member) => member.userId), [members]);
  const explorerGroupIds = React.useMemo(() => (groupId ? [groupId] : []), [groupId]);

  React.useEffect(() => {
    try {
      if (groupId) {
        return listenToGroup(
          groupId,
          (group) => {
            setGroup(group);
            setIsAdmin(group.adminIds.includes(currentUserId));
            setLoadingGroup(false);
          },
          (error) => logError(error)
        );
      }
    } catch (error) {
      logError(error);
    }
  }, [currentUserId, groupId]);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () =>
        isAdmin ? (
          <IconButton
            icon="dots-vertical"
            onPress={() => showActionSheet()}
            testID="navigation.Header.GroupActionsIconButton"
          />
        ) : null,
    });
  }, [isAdmin, navigation, showActionSheet]);

  return (
    <ScreenContainer loading={loadingGroup}>
      <GroupHeader
        group={group}
        canViewMembers={canViewGroupContent}
        onMembersPress={() => appNavigation.openGroupMembers(groupId)}
      />
      {group ? (
        <Divider style={styles.headerDivider} testID="screens.ViewGroupScreen.HeaderDivider" />
      ) : null}
      {canViewGroupContent ? (
        <Explorer
          getPlanDisplayProfile={(plan) => ({
            displayName: plan.user.displayName,
            entityType: 'user',
            photoUrl: plan.user.photoUrl,
            onPress: () => appNavigation.openProfile(plan.userId),
          })}
          mapDataRefreshKey={mapDataRefreshKey}
          mapTestID="screens.ViewGroupScreen.Map"
          onChatPress={appNavigation.openChat}
          onCreatePlanPress={appNavigation.openCreatePlan}
          onGroupPress={appNavigation.openGroup}
          onPlanNavigationPress={appNavigation.openPlan}
          onUserNavigationPress={appNavigation.openProfile}
          queryGroupIds={explorerGroupIds}
          queryUserIds={explorerUserIds}
          searchTargets={GROUP_SEARCH_TARGETS}
        />
      ) : (
        <View style={styles.placeholder} testID="screens.ViewGroupScreen.NotMemberPlaceholder">
          <Placeholder title="Not a member" icon="lock" message="Join to view" />
          <JoinGroupButton groupId={groupId} variant="small" style={styles.joinButton} />
        </View>
      )}
    </ScreenContainer>
  );
}

type GroupHeaderProps = {
  group?: Group;
  canViewMembers: boolean;
  onMembersPress: () => void;
};

function GroupHeader({ group, canViewMembers, onMembersPress }: GroupHeaderProps) {
  if (!group) return null;

  return (
    <View style={styles.groupHeader}>
      <UserAvatar
        displayName={group.displayName}
        photoUrl={group.photoUrl}
        size={GROUP_HEADER_AVATAR_SIZE}
        style={styles.groupHeaderAvatar}
      />
      <View style={styles.groupHeaderTextContainer}>
        <Title numberOfLines={1} style={styles.groupHeaderTitle}>
          {group.displayName}
        </Title>
        {group.description ? (
          <Caption numberOfLines={2} style={styles.groupHeaderCaption}>
            {group.description}
          </Caption>
        ) : null}
        <View style={styles.groupHeaderMeta}>
          <Icon name="account-multiple" size={GROUP_HEADER_ICON_SIZE} />
          <MembersCaption
            canViewMembers={canViewMembers}
            numMembers={group.numMembers || 0}
            onPress={onMembersPress}
          />
          {group.verified ? (
            <>
              <Icon name="check-circle" size={GROUP_HEADER_ICON_SIZE} />
              <Caption numberOfLines={1} style={styles.groupHeaderMetaText}>
                Verified
              </Caption>
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function MembersCaption({
  canViewMembers,
  numMembers,
  onPress,
}: {
  canViewMembers: boolean;
  numMembers: number;
  onPress: () => void;
}) {
  const caption = `${numMembers} members`;

  if (!canViewMembers) {
    return (
      <Caption numberOfLines={1} style={styles.groupHeaderMetaText}>
        {caption}
      </Caption>
    );
  }

  return (
    <PressableOpacity accessibilityLabel="View members" onPress={onPress}>
      <Caption numberOfLines={1} style={styles.groupHeaderMetaText}>
        {caption}
      </Caption>
    </PressableOpacity>
  );
}

const styles = StyleSheet.create({
  groupHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
    padding: 10,
    width: '100%',
  },
  groupHeaderAvatar: {
    flexShrink: 0,
    marginRight: 10,
  },
  groupHeaderCaption: {
    flexShrink: 1,
  },
  groupHeaderMeta: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: 2,
  },
  groupHeaderMetaText: {
    marginLeft: 4,
    marginRight: 10,
  },
  groupHeaderTextContainer: {
    flex: 1,
    minWidth: 0,
  },
  groupHeaderTitle: {
    lineHeight: 24,
  },
  headerDivider: {
    alignSelf: 'center',
    marginBottom: 8,
    width: '80%',
  },
  joinButton: {
    minWidth: 120,
  },
  placeholder: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    width: '100%',
  },
});
